import type { Location } from "react-router-dom";
import type { RoutedOverlayChain } from "./RoutedOverlayChainContext";

export interface RoutedOverlayEntryState {
  /**
   * Pathnames of the entries below this one that were opened in-app as a chain, oldest
   * first. The last item is the entry this overlay was opened from.
   */
  openers: string[];
  /** Identifies an in-app open for the current page load (see `wasOpenedInApp`). */
  nonce: string;
}

export interface RoutedOverlayLocationState {
  routedOverlay?: RoutedOverlayEntryState;
}

// Module-scoped so it resets on a full page load; lets transient overlays tell an
// in-app open apart from a deep link or refresh (history.state survives reloads).
const inAppOpenNonces = new Set<string>();
// Clearing native history does not update React Router's current location object.
const consumedHostStateKeys = new WeakMap<object, Set<string>>();
let nonceCounter = 0;

export const markHostNavigationStateConsumed = (location: Location): void => {
  const { state, key } = location;
  if (!state || typeof state !== "object" || Array.isArray(state)) return;

  const keys = consumedHostStateKeys.get(state) ?? new Set<string>();
  keys.add(key);
  consumedHostStateKeys.set(state, keys);
};

export const getOverlayEntryState = (
  location: Location,
): RoutedOverlayEntryState | undefined =>
  (location.state as RoutedOverlayLocationState | null)?.routedOverlay;

export const wasOpenedInApp = (location: Location): boolean => {
  const entry = getOverlayEntryState(location);
  return !!entry && inAppOpenNonces.has(entry.nonce);
};

/**
 * The page's own navigation state at `location` (for example deep-link selections passed to
 * `navigate`), without the routed overlay entry, or `undefined` when there is none.
 */
export const getHostNavigationState = (
  location: Location,
): Record<string, unknown> | undefined => {
  const { state } = location;
  if (
    !state ||
    typeof state !== "object" ||
    Array.isArray(state) ||
    consumedHostStateKeys.get(state)?.has(location.key)
  ) {
    return undefined;
  }
  const hostState: Record<string, unknown> = { ...state };
  delete hostState.routedOverlay;
  return Object.keys(hostState).length > 0 ? hostState : undefined;
};

/**
 * State for an overlay entry opened in-app from `opener`. Keeps the opener's own
 * navigation state so the page under the overlay reads the same state while it is open
 * and after Back.
 */
export const createOverlayEntryState = (
  opener: Location,
): RoutedOverlayLocationState & Record<string, unknown> => {
  nonceCounter += 1;
  const nonce = `${Date.now().toString(36)}-${nonceCounter}`;
  inAppOpenNonces.add(nonce);
  const inheritedOpeners = wasOpenedInApp(opener)
    ? (getOverlayEntryState(opener)?.openers ?? [])
    : [];
  return {
    ...getHostNavigationState(opener),
    routedOverlay: { openers: [...inheritedOpeners, opener.pathname], nonce },
  };
};

const isBelowPath = (pathname: string, base: string): boolean => {
  const normalizedBase = joinPath(base);
  const normalizedPath = joinPath(pathname);
  return normalizedBase === "/"
    ? normalizedPath !== "/"
    : normalizedPath.startsWith(`${normalizedBase}/`);
};

/**
 * Number of history entries to step back so an overlay closing to `closeTo` also
 * dismisses any overlays opened above it, or `undefined` when the entries were not all
 * opened in-app during this page load and the caller should replace instead.
 */
export const getOverlayCloseDelta = (
  location: Location,
  closeTo: string,
): number | undefined => {
  if (!wasOpenedInApp(location)) {
    return undefined;
  }
  const openers = getOverlayEntryState(location)?.openers ?? [];
  for (let index = openers.length - 1; index >= 0; index -= 1) {
    if (!isBelowPath(openers[index], closeTo)) {
      return openers.length - index;
    }
  }
  return undefined;
};

/** Joins path parts into a normalized absolute pathname without a trailing slash. */
export const joinPath = (...parts: Array<string | undefined | null>): string => {
  const segments = parts
    .filter((part): part is string => !!part)
    .flatMap((part) => part.split("/"))
    .filter(Boolean);
  return `/${segments.join("/")}`;
};

/**
 * Number of leading `parts` that spell out `pathPrefix`, or 0 when they do not start with
 * it, so overlay segments are never matched inside a page prefix such as a site prefix.
 */
export const countPathPrefixParts = (
  parts: readonly string[],
  pathPrefix: string | undefined,
): number => {
  const prefixParts = pathPrefix?.split("/").filter(Boolean) ?? [];
  return prefixParts.every((part, index) => parts[index] === part)
    ? prefixParts.length
    : 0;
};

export interface GlobalOverlayBasePathOptions {
  /** Only match a `segment` part followed by an id, as for a `{segment}/{id}` drawer. */
  requireId?: boolean;
  /**
   * Levels parsed from `pathname`; their ids and tabs, and the chain's `pathPrefix`, are
   * never taken for `segment`.
   */
  chain?: RoutedOverlayChain;
}

const partCount = (path: string) => path.split("/").filter(Boolean).length;

/**
 * Returns the path a global overlay nests under: everything before the first `segment`
 * part after the chain's `pathPrefix`, or the whole pathname when the segment is absent.
 * Without `requireId` a trailing segment also matches, so
 * `/services/invoices/email-invoices` resolves to `/services/invoices`. A `chain` level for
 * `segment` resolves to its parent path.
 */
export const getGlobalOverlayBasePath = (
  pathname: string,
  segment: string,
  { requireId = false, chain }: GlobalOverlayBasePathOptions = {},
): string => {
  const level = chain?.levels.find(
    (candidate) => candidate.segment === segment,
  );
  if (level) {
    return level.parentPath;
  }
  const parts = pathname.split("/").filter(Boolean);
  const prefixEnd = countPathPrefixParts(parts, chain?.pathPrefix);
  const [chainStart, chainEnd] =
    chain && chain.levels.length > 0
      ? [partCount(chain.basePath), partCount(chain.tipPath)]
      : [0, 0];
  const index = parts.findIndex(
    (part, partIndex) =>
      part === segment &&
      partIndex >= prefixEnd &&
      (partIndex < chainStart || partIndex >= chainEnd) &&
      (!requireId || partIndex < parts.length - 1),
  );
  return joinPath(...(index >= 0 ? parts.slice(0, index) : parts));
};

export const encodeRouteSegment = (value: string | number): string =>
  encodeURIComponent(String(value));

// An empty value drops its path segment, and browsers normalize "." and ".." segments away.
const UNSAFE_ROUTE_SEGMENTS = new Set(["", ".", ".."]);

/** Whether a value (e.g. an entity id) survives as its own URL path segment. */
export const isRouteSafeSegment = (value: string | number): boolean =>
  !UNSAFE_ROUTE_SEGMENTS.has(String(value));

export const decodeRouteSegment = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/** Converts a display label to a kebab-case route slug, e.g. "Sales Play" -> "sales-play". */
export const toRouteSlug = (label: string): string =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
